import React, { useEffect, useState, useRef } from "react";
import OutfitUnitView from "presentational/common/outfitUnit/OutfitUnitView";
import CommonMoreRequestButton from "presentational/common/CommonMoreRequestButton";
import { getDeviceType } from "lib/deviceInfo";
import BrowserResizeDetector from "presentational/common/BrowserResizeDetector";
import Typography from "@material-ui/core/Typography";
import BottomScrollListener from "react-bottom-scroll-listener";
import config from "lib/fr-config";
import PropTypes from "prop-types";

const desktopCss = config.css.desktop;
const mobileCss = config.css.mobile;
const commonCss = config.css.commonDevice;

const FIXED_VALUE = (() => {
  const {
    mobile: maxWidthOfThumbnailForMobile,
    desktop: maxWidthOfThumbnailForDesktop
  } = config.outfitImage.MAXIMUM_WIDTH_OF_THUMBNAIL;
  const {
    mobile: maxWidthOfThumbnailForMobileForSmall,
    desktop: maxWidthOfThumbnailForDesktopForSmall
  } = config.outfitImage.MAXIMUM_WIDTH_OF_THUMBNAIL_FOR_SMALL;
  const {
    rightMarginProportionToSidePadding: rightMarginRatioForMobile,
    bottomMarginProportionToSidePadding: bottomMarginRatioForMobile
  } = mobileCss.outfitUnit;
  const {
    rightMarginProportionToSidePadding: rightMarginRatioForDesktop,
    bottomMarginProportionToSidePadding: bottomMarginRatioForDesktop
  } = desktopCss.outfitUnit;
  const { sidePadding: sidePaddingForMobile } = mobileCss;
  const { sidePadding: sidePaddingForDesktop } = desktopCss;
  return {
    desktop: {
      maxWidthOfThumbnail: maxWidthOfThumbnailForDesktop,
      maxWidthOfThumbnailForSmall: maxWidthOfThumbnailForDesktopForSmall,
      rightMarginRatio: rightMarginRatioForDesktop,
      bottomMarginRatio: bottomMarginRatioForDesktop,
      sidePadding: sidePaddingForDesktop
    },
    mobile: {
      maxWidthOfThumbnail: maxWidthOfThumbnailForMobile,
      maxWidthOfThumbnailForSmall: maxWidthOfThumbnailForMobileForSmall,
      rightMarginRatio: rightMarginRatioForMobile,
      bottomMarginRatio: bottomMarginRatioForMobile,
      sidePadding: sidePaddingForMobile
    }
  };
})();

OutfitListView.propTypes = {
  list: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isEnd: PropTypes.bool.isRequired,
  callNextListFn: PropTypes.func.isRequired,
  emptyListText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  shortBottomPadding: PropTypes.bool,
  displayTooltipForGuest: PropTypes.bool,

  selectMode: PropTypes.bool,
  markedList: PropTypes.array,
  onClickInSelectMode: PropTypes.func,

  smallUnit: PropTypes.bool
};

OutfitListView.defaultProps = {
  emptyListText: null,
  shortBottomPadding: false,
  displayTooltipForGuest: false,

  selectMode: false,
  markedList: [],
  onClickInSelectMode: () => {},

  smallUnit: false
};

function OutfitListView(props) {
  const {
    list,
    isLoading,
    isEnd,
    callNextListFn,
    emptyListText,
    shortBottomPadding,
    displayTooltipForGuest,
    selectMode,
    markedList,
    onClickInSelectMode,
    smallUnit
  } = props;

  const [numOfUnitsPerColumn, setNumOfUnitsPerColumn] = useState(1);

  const hiddenContainer = useRef();

  useEffect(() => {
    checkNumOfUnitsPerColumn();
  }, [numOfUnitsPerColumn]);

  const getBottomPaddingPX = () => {
    if (shortBottomPadding) {
      return getDeviceType() === "mobile"
        ? mobileCss.standardPaddingBetweenContent
        : desktopCss.standardPaddingBetweenContent;
    } else {
      return getDeviceType() === "mobile"
        ? mobileCss.paddingOfScrollEnd
        : desktopCss.paddingOfScrollEnd;
    }
  };

  const checkNumOfUnitsPerColumn = () => {
    const newNumOfUnitsPerColumn = getMinNumOfUnitsOfOneColumn();
    if (numOfUnitsPerColumn !== newNumOfUnitsPerColumn) {
      setNumOfUnitsPerColumn(newNumOfUnitsPerColumn);
    }
  };

  const getMinNumOfUnitsOfOneColumn = () => {
    const {
      maxWidthOfThumbnail,
      maxWidthOfThumbnailForSmall,
      rightMarginRatio,
      sidePadding
    } = FIXED_VALUE[getDeviceType()];
    const maxWidth = smallUnit
      ? maxWidthOfThumbnailForSmall
      : maxWidthOfThumbnail;
    const widthOfRoot = hiddenContainer.current.clientWidth;
    const padding = sidePadding * rightMarginRatio;

    const minNum = Math.ceil((widthOfRoot + padding) / (maxWidth + padding));

    return minNum;
  };

  const getNextList = () => {
    if (!list || list.length === 0) return null;

    if (!isEnd && !isLoading) {
      callNextListFn();
    }
  };

  const emptyListElement = () => {
    if (!emptyListText) return null;

    return (
      <div style={css.emptyListTextWrapper}>
        <Typography variant="h4" style={css.emptyListText}>
          {emptyListText}
        </Typography>
      </div>
    );
  };

  const isMarkedUnit = outfitInfo => {
    if (!selectMode || markedList.length === 0 || !outfitInfo) return false;

    for (let i = 0; i < markedList.length; i++) {
      const markedItemInfo = markedList[i];
      if (markedItemInfo.outfitHash === outfitInfo.outfitHash) return true;
    }
    return false;
  };

  const unitList = () => {
    const returnListArr = [];

    const oneUnit = (infoObj, isFirstUnit) =>
      !infoObj ? (
        <div />
      ) : (
        <OutfitUnitView
          outfitHash={infoObj.outfitHash}
          username={infoObj.username}
          image={infoObj.image}
          displayOutfitUnitTooltip={displayTooltipForGuest && isFirstUnit}
          selectMode={selectMode}
          isMarkedOutfit={isMarkedUnit(infoObj)}
          onClickInSelectMode={onClickInSelectMode}
        />
      );

    for (let i = 0; i < list.length; i = i + numOfUnitsPerColumn) {
      const unitArr = [];

      for (let j = 0; j < numOfUnitsPerColumn; j++) {
        const isFirstUnit = i + j === 0 ? true : false;
        unitArr.push(
          <div style={css.unit} key={j}>
            {oneUnit(list[i + j], isFirstUnit)}
          </div>
        );
      }

      returnListArr.push(
        <div style={css.unitRow} key={i}>
          {unitArr}
        </div>
      );
    }

    return isEnd && returnListArr.length === 0
      ? emptyListElement()
      : returnListArr;
  };

  const getCssXL = () => {
    const { rightMarginRatio, bottomMarginRatio, sidePadding } =
      FIXED_VALUE[getDeviceType()];
    return {
      root: {
        marginBottom: getBottomPaddingPX()
      },
      unitArea: {
        display: "flex",
        flexDirection: "column"
      },
      unitRow: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: sidePadding * bottomMarginRatio
      },
      unit: {
        width: `calc((100% - (${
          rightMarginRatio * sidePadding
        }px * (${numOfUnitsPerColumn} - 1)))/ ${numOfUnitsPerColumn})`
      },
      emptyListTextWrapper: {
        display: "flex",
        justifyContent: "center",
        marginBottom: 50
      },
      emptyListText: { fontSize: 16 },
      requestBtnWrapper: {
        display: "flex",
        justifyContent: "center",
        paddingTop: 100
      }
    };
  };
  const getCssXS = () => {
    const { rightMarginRatio, bottomMarginRatio, sidePadding } =
      FIXED_VALUE[getDeviceType()];
    return {
      root: {
        marginBottom: getBottomPaddingPX()
      },
      unitArea: {
        display: "flex",
        flexDirection: "column"
      },
      unitRow: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: sidePadding * bottomMarginRatio
      },
      unit: {
        width: `calc((100% - (${
          rightMarginRatio * sidePadding
        }px * (${numOfUnitsPerColumn} - 1)))/ ${numOfUnitsPerColumn})`
      },
      emptyListTextWrapper: {
        display: "flex",
        justifyContent: "center",
        marginBottom: 50
      },
      emptyListText: { fontSize: 14 },
      requestBtnWrapper: {
        display: "flex",
        justifyContent: "center"
      }
    };
  };

  const css = getDeviceType() === "mobile" ? getCssXS() : getCssXL();
  const scrollOffset =
    getDeviceType() === "mobile"
      ? commonCss.scrollListenerOffset + getBottomPaddingPX()
      : commonCss.scrollListenerOffset;

  return (
    <div style={css.root}>
      <BottomScrollListener
        onBottom={getNextList}
        debounce={10}
        offset={scrollOffset}
      />
      <BrowserResizeDetector onResize={checkNumOfUnitsPerColumn} />
      <div style={css.unitArea}>
        <div style={{ width: "100%" }} ref={hiddenContainer}></div>
        {unitList()}
        {!isEnd && (
          <div style={css.requestBtnWrapper}>
            <CommonMoreRequestButton
              onClick={getNextList}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default OutfitListView;
